import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy, addDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';
import RecruitmentForm from './RecruitmentForm';

interface RecruitmentApplicationsProps {
  domain: string;
}

interface Application {
  id: string;
  applicationId: string;
  candidateName: string;
  email: string;
  phone: string;
  position: string;
  department: string;
  qualification: string;
  experience: string;
  currentEmployer: string;
  expectedSalary: string;
  address: string;
  skills: string;
  status: 'pending' | 'under-review' | 'interview-scheduled' | 'hired' | 'rejected';
  appliedOn: string;
  reviewedOn?: string;
  remarks?: string;
}

const RecruitmentApplications: React.FC<RecruitmentApplicationsProps> = ({ domain }) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    interviewDate: '',
    interviewTime: '',
    venue: '',
    interviewer: '',
    interviewType: 'in-person',
  });

  useEffect(() => {
    if (domain) loadApplications();
  }, [domain]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const applicationsRef = collection(db, `school-data/${domain}/recruitment-applications`);
      const q = query(applicationsRef, orderBy('appliedOn', 'desc'));
      const snapshot = await getDocs(q);

      const appsList: Application[] = [];
      snapshot.forEach((docSnap) => {
        appsList.push({
          id: docSnap.id,
          ...docSnap.data(),
        } as Application);
      });

      setApplications(appsList);
    } catch (error: any) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appId: string, newStatus: Application['status'], remarks?: string) => {
    try {
      const appRef = doc(db, `school-data/${domain}/recruitment-applications`, appId);
      await updateDoc(appRef, {
        status: newStatus,
        reviewedOn: new Date().toISOString(),
        remarks: remarks || '',
      });

      await loadApplications();
      setShowModal(false);
      setSelectedApp(null);
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(`Failed to update status: ${error.message}`);
    }
  };

  const deleteApplication = async (appId: string) => {
    if (!confirm('Are you sure you want to delete this application?')) return;

    try {
      const appRef = doc(db, `school-data/${domain}/recruitment-applications`, appId);
      await deleteDoc(appRef);
      await loadApplications();
    } catch (error: any) {
      console.error('Error deleting application:', error);
      alert(`Failed to delete application: ${error.message}`);
    }
  };

  const scheduleInterview = async () => {
    if (!selectedApp || !interviewForm.interviewDate || !interviewForm.interviewTime || !interviewForm.venue) {
      alert('Please fill all required fields');
      return;
    }

    try {
      const interviewData = {
        applicationId: selectedApp.applicationId,
        candidateName: selectedApp.candidateName,
        position: selectedApp.position,
        department: selectedApp.department,
        interviewDate: interviewForm.interviewDate,
        interviewTime: interviewForm.interviewTime,
        venue: interviewForm.venue,
        interviewer: interviewForm.interviewer,
        interviewType: interviewForm.interviewType,
        status: 'scheduled',
        scheduledOn: new Date().toISOString(),
      };

      const interviewsRef = collection(db, `school-data/${domain}/recruitment-interviews`);
      await addDoc(interviewsRef, interviewData);

      const appRef = doc(db, `school-data/${domain}/recruitment-applications`, selectedApp.id);
      await updateDoc(appRef, { status: 'interview-scheduled' });

      alert('Interview scheduled successfully!');
      setShowInterviewModal(false);
      setShowModal(false);
      setSelectedApp(null);
      setInterviewForm({ interviewDate: '', interviewTime: '', venue: '', interviewer: '', interviewType: 'in-person' });
      await loadApplications();
    } catch (error: any) {
      console.error('Error scheduling interview:', error);
      alert(`Failed to schedule interview: ${error.message}`);
    }
  };

  const exportToExcel = () => {
    const exportData = filteredApplications.map(app => ({
      'Application ID': app.applicationId,
      'Candidate Name': app.candidateName,
      'Position': app.position,
      'Department': app.department,
      'Email': app.email,
      'Phone': app.phone,
      'Experience': app.experience,
      'Qualification': app.qualification,
      'Expected Salary': app.expectedSalary,
      'Status': app.status.replace('-', ' ').toUpperCase(),
      'Applied On': new Date(app.appliedOn).toLocaleDateString(),
      'Remarks': app.remarks || '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Recruitment');
    XLSX.writeFile(wb, `recruitment_applications_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const filteredApplications = applications.filter(app => {
    const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
    const matchesSearch =
      app.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.applicationId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.phone.includes(searchTerm);

    return matchesStatus && matchesSearch;
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-secondary';
      case 'under-review': return 'bg-info';
      case 'interview-scheduled': return 'bg-warning';
      case 'hired': return 'bg-success';
      case 'rejected': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const counts = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    underReview: applications.filter(a => a.status === 'under-review').length,
    interviewScheduled: applications.filter(a => a.status === 'interview-scheduled').length,
    hired: applications.filter(a => a.status === 'hired').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
  };

  if (showForm) {
    return (
      <div>
        <div className="mb-3">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowForm(false)}>
            <i className="bi bi-arrow-left me-2"></i>Back to Applications
          </button>
        </div>
        <RecruitmentForm domain={domain} />
      </div>
    );
  }

  return (
    <div>
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-list-ul me-2 text-primary-custom"></i>
              Recruitment Applications
            </h5>
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
                <i className="bi bi-plus-circle me-2"></i>
                New Application
              </button>
              <button className="btn btn-success btn-sm" onClick={exportToExcel}>
                <i className="bi bi-file-earmark-excel me-2"></i>
                Export to Excel
              </button>
            </div>
          </div>
        </div>

        <div className="card-body">
          {/* Statistics */}
          <div className="row g-3 mb-4">
            <div className="col-md-2">
              <div className="card bg-primary bg-opacity-10 border-0">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-primary">{counts.total}</h3>
                  <small className="text-muted">Total</small>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card bg-secondary bg-opacity-10 border-0">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-secondary">{counts.pending}</h3>
                  <small className="text-muted">Pending</small>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card bg-info bg-opacity-10 border-0">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-info">{counts.underReview}</h3>
                  <small className="text-muted">Under Review</small>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card bg-warning bg-opacity-10 border-0">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-warning">{counts.interviewScheduled}</h3>
                  <small className="text-muted">Interview</small>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card bg-success bg-opacity-10 border-0">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-success">{counts.hired}</h3>
                  <small className="text-muted">Hired</small>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card bg-danger bg-opacity-10 border-0">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-danger">{counts.rejected}</h3>
                  <small className="text-muted">Rejected</small>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Filter by Status</label>
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Applications</option>
                <option value="pending">Pending</option>
                <option value="under-review">Under Review</option>
                <option value="interview-scheduled">Interview Scheduled</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="col-md-8">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, application ID, position, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Applications Table */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading applications...</p>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-1 text-muted"></i>
              <p className="mt-3 text-muted">No applications found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Application ID</th>
                    <th>Candidate Name</th>
                    <th>Position</th>
                    <th>Department</th>
                    <th>Experience</th>
                    <th>Applied On</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map((app) => (
                    <tr key={app.id}>
                      <td><strong>{app.applicationId}</strong></td>
                      <td>{app.candidateName}</td>
                      <td>{app.position}</td>
                      <td><span className="badge bg-primary">{app.department}</span></td>
                      <td>{app.experience}</td>
                      <td>{new Date(app.appliedOn).toLocaleDateString()}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(app.status)}`}>
                          {app.status.replace('-', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => { setSelectedApp(app); setShowModal(true); }}
                            title="View Details"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => deleteApplication(app.id)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Application Details Modal */}
      {showModal && selectedApp && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Application Details - {selectedApp.applicationId}</h5>
                <button type="button" className="btn-close" onClick={() => { setShowModal(false); setSelectedApp(null); }}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Candidate Name</label>
                    <p>{selectedApp.candidateName}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Position Applied</label>
                    <p>{selectedApp.position}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Department</label>
                    <p>{selectedApp.department}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Qualification</label>
                    <p>{selectedApp.qualification}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Experience</label>
                    <p>{selectedApp.experience}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Current Employer</label>
                    <p>{selectedApp.currentEmployer || 'N/A'}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Expected Salary</label>
                    <p>{selectedApp.expectedSalary || 'N/A'}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Skills</label>
                    <p>{selectedApp.skills || 'N/A'}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Email</label>
                    <p>{selectedApp.email}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Phone</label>
                    <p>{selectedApp.phone}</p>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold">Address</label>
                    <p>{selectedApp.address || 'N/A'}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Current Status</label>
                    <p>
                      <span className={`badge ${getStatusBadgeClass(selectedApp.status)}`}>
                        {selectedApp.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Applied On</label>
                    <p>{new Date(selectedApp.appliedOn).toLocaleString()}</p>
                  </div>
                  {selectedApp.remarks && (
                    <div className="col-12">
                      <label className="form-label fw-bold">Remarks</label>
                      <p>{selectedApp.remarks}</p>
                    </div>
                  )}
                </div>

                <hr />

                <div className="mb-3">
                  <label className="form-label fw-bold">Update Status</label>
                  <div className="d-flex gap-2 flex-wrap">
                    <button className="btn btn-secondary btn-sm" onClick={() => updateStatus(selectedApp.id, 'pending')}>Pending</button>
                    <button className="btn btn-info btn-sm" onClick={() => updateStatus(selectedApp.id, 'under-review')}>Under Review</button>
                    <button className="btn btn-warning btn-sm" onClick={() => { setShowModal(false); setShowInterviewModal(true); }}>Schedule Interview</button>
                    <button className="btn btn-success btn-sm" onClick={() => updateStatus(selectedApp.id, 'hired')}>Hire</button>
                    <button className="btn btn-danger btn-sm" onClick={() => updateStatus(selectedApp.id, 'rejected')}>Reject</button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setSelectedApp(null); }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interview Scheduling Modal */}
      {showInterviewModal && selectedApp && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Schedule Interview</h5>
                <button type="button" className="btn-close" onClick={() => { setShowInterviewModal(false); setShowModal(true); }}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <strong>Candidate:</strong> {selectedApp.candidateName}<br />
                  <strong>Position:</strong> {selectedApp.position}<br />
                  <strong>Application ID:</strong> {selectedApp.applicationId}
                </div>
                <div className="mb-3">
                  <label className="form-label">Interview Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={interviewForm.interviewDate}
                    onChange={(e) => setInterviewForm({ ...interviewForm, interviewDate: e.target.value })}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Interview Time *</label>
                  <input
                    type="time"
                    className="form-control"
                    value={interviewForm.interviewTime}
                    onChange={(e) => setInterviewForm({ ...interviewForm, interviewTime: e.target.value })}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Venue *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={interviewForm.venue}
                    onChange={(e) => setInterviewForm({ ...interviewForm, venue: e.target.value })}
                    placeholder="e.g., Principal's Office, Conference Room"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Interview Type</label>
                  <select
                    className="form-select"
                    value={interviewForm.interviewType}
                    onChange={(e) => setInterviewForm({ ...interviewForm, interviewType: e.target.value })}
                  >
                    <option value="in-person">In Person</option>
                    <option value="video-call">Video Call</option>
                    <option value="phone">Phone</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Interviewer</label>
                  <input
                    type="text"
                    className="form-control"
                    value={interviewForm.interviewer}
                    onChange={(e) => setInterviewForm({ ...interviewForm, interviewer: e.target.value })}
                    placeholder="Name of interviewer(s)"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowInterviewModal(false); setShowModal(true); }}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={scheduleInterview}>
                  <i className="bi bi-calendar-check me-2"></i>Schedule Interview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruitmentApplications;
